import { Save, X } from "lucide-react";
import { useState } from "react";
import { saveCosto } from "../../api";
import { payloadCosto } from "./costosData";
import { CATEGORIAS_MANUALES, fmtClp, mesLargo, type Moneda } from "./costosFormat";
import { useMontoCosto } from "./useMontoCosto";

export interface CostoPrefill {
  id?: string;
  categoria?: string;
  concepto?: string;
  monto?: number;
  /** Costo recurrente: cuenta todos los meses desde `mesOrigen`. */
  periodico?: boolean;
  mesOrigen?: string;
  hasta?: string;
}

interface Props {
  mes: string;
  prefill: CostoPrefill;
  onSaved: (mensaje: string) => void;
  onCancel: () => void;
}

const MONEDAS: Moneda[] = ["CLP", "USD"];

/** Alta y edición de un costo manual del mes, en CLP o USD, único o recurrente. Se muestra en línea en escritorio. */
export function CostoForm({ mes, prefill, onSaved, onCancel }: Props) {
  const [categoria, setCategoria] = useState(prefill.categoria ?? CATEGORIAS_MANUALES[0]);
  const [concepto, setConcepto] = useState(prefill.concepto ?? "");
  const monto = useMontoCosto(prefill.monto);
  const [periodico, setPeriodico] = useState(prefill.periodico ?? false);
  const [nota, setNota] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const admitePeriodico = categoria !== "GCP";
  const puedeTerminar = !!prefill.periodico && !!prefill.mesOrigen && prefill.mesOrigen < mes;
  const valido = concepto.trim().length > 0 && !isNaN(monto.montoClp) && monto.montoClp >= 0;

  const guardar = async (terminar = false) => {
    if (!valido || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await saveCosto(payloadCosto({ mes, prefill, categoria, concepto, montoClp: monto.montoClp, nota: monto.notaFinal(nota), periodico: admitePeriodico && periodico, terminar }));
      onSaved(terminar ? "Costo terminado" : prefill.id ? "Costo actualizado" : "Costo agregado");
    } catch (err: any) {
      setError(err?.message || "No se pudo guardar el costo.");
      setGuardando(false);
    }
  };

  return (
    <form className="cst-form" onSubmit={(e) => { e.preventDefault(); void guardar(); }}>
      <div className="cst-form-head">
        <div>
          <span className="sbx-tag">{prefill.id ? "Editando" : "Nuevo costo"}</span>
          <h3>{mesLargo(mes)}</h3>
        </div>
        <button type="button" className="cst-icon" onClick={onCancel} aria-label="Cerrar"><X size={18} /></button>
      </div>
      <label>
        Categoría
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          {CATEGORIAS_MANUALES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label>
        Concepto
        <input type="text" placeholder="Ej. Vultr Santiago" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
      </label>
      <div className="cst-field">
        <span className="cst-field-label">Monto{admitePeriodico && periodico ? " de cada mes" : " del mes"}</span>
        <div className="cst-amount">
          <div className="cst-seg" role="radiogroup" aria-label="Moneda">
            {MONEDAS.map((m) => (
              <button key={m} type="button" role="radio" aria-checked={monto.moneda === m} className={monto.moneda === m ? "is-on" : ""} onClick={() => monto.cambiarMoneda(m)}>{m}</button>
            ))}
          </div>
          <span className="cst-amount-box">
            <span aria-hidden="true">{monto.esUsd ? "US$" : "$"}</span>
            <input type="text" inputMode={monto.esUsd ? "decimal" : "numeric"} autoComplete="off" placeholder="0" aria-label="Monto" value={monto.texto} onChange={(e) => monto.escribir(e.target.value)} />
          </span>
        </div>
        {monto.esUsd && (
          <p className={monto.dolarFalla ? "cst-error" : "cst-hint"}>
            {monto.dolarFalla ? "No se pudo obtener el dólar del día; ingresa el monto en CLP."
              : !monto.dolar ? "Buscando el dólar del día…"
              : isNaN(monto.montoClp) ? `Dólar del ${monto.dolar.fecha}: $${String(monto.dolar.valor).replace(".", ",")}`
              : `≈ ${fmtClp(monto.montoClp)} · ${monto.detalleUsd}`}
          </p>
        )}
      </div>
      {admitePeriodico && (
        <div className="cst-field">
          <span className="cst-field-label">Periodicidad</span>
          <div className="cst-seg" role="radiogroup" aria-label="Periodicidad">
            <button type="button" role="radio" aria-checked={!periodico} className={!periodico ? "is-on" : ""} onClick={() => setPeriodico(false)}>Una sola vez</button>
            <button type="button" role="radio" aria-checked={periodico} className={periodico ? "is-on" : ""} onClick={() => setPeriodico(true)}>Recurrente · todos los meses</button>
          </div>
        </div>
      )}
      <label>
        Nota (opcional)
        <input type="text" value={nota} onChange={(e) => setNota(e.target.value)} />
      </label>
      {categoria === "GCP" && <p className="cst-hint">Un costo de GCP cargado a mano reemplaza a la exportación solo en meses que la exportación no cubre completos.</p>}
      {admitePeriodico && periodico && prefill.id && <p className="cst-hint">Editar cambia el monto en todos los meses del costo recurrente.</p>}
      {error && <p className="cst-error">{error}</p>}
      <button type="submit" className="cst-primary" disabled={!valido || guardando}>
        <Save size={16} />
        {prefill.id ? "Actualizar" : "Agregar"}
      </button>
      {puedeTerminar && (
        <button type="button" className="cst-secondary" disabled={!valido || guardando} onClick={() => void guardar(true)}>Terminar desde {mesLargo(mes)}</button>
      )}
    </form>
  );
}
